import type { Store, SubscriptionAccess } from '@fabric-app/types/pricing/pricing.types';
import type { ApiUserMe } from '@fabric-app/types/user';
import { isDefined } from '@fabric-app/utils/guards';
import { capitalizeFirstLetter } from '@fabric-app/utils/text';
import { SubscriptionTier } from '@fabric/woody-client';
import type { CustomerInfo } from '@revenuecat/purchases-capacitor';

type Context = {
  user: ApiUserMe;
  customerInfo?: CustomerInfo;
  isRevenueCatAvailable: boolean;
  currentDeviceStore: Store;
};

export const getSubscriptionAccess = ({
  user,
  customerInfo,
  isRevenueCatAvailable,
  currentDeviceStore,
}: Context): SubscriptionAccess => {
  /**
   * This converts the woody user's subscription tier to the entitlement id used by RevenueCat.
   * Woody uses SubscriptionTier.BASIC_LEGACY for the Basic tier, but RevenueCat uses 'Basic'.
   * The rest is the same but first letter capitalized, so e.g. 'pro' becomes 'Pro'.
   */
  const userEntitlementId =
    user.subscription.tier !== SubscriptionTier.FREE && isDefined(user.subscription.tier)
      ? user.subscription.tier === SubscriptionTier.BASIC_LEGACY
        ? 'Basic'
        : capitalizeFirstLetter(user.subscription.tier)
      : null;

  /**
   * Get the user's entitlement from RevenueCat, if it exists.
   */
  const userEntitlement = userEntitlementId
    ? customerInfo?.entitlements.active[userEntitlementId]
    : null;

  const userTier = user.subscription.tier ?? SubscriptionTier.FREE;
  const userStore =
    userTier !== SubscriptionTier.FREE ? ((user.subscription.store as Store) ?? 'stripe') : null;

  // Lifetime users can't alter or manage their subscription
  if (user.subscription.tier === SubscriptionTier.LIFETIME)
    return {
      canAlterSubscription: {
        allowed: false,
        reason: SubscriptionTier.LIFETIME,
      },
      canManageSubscription: {
        allowed: false,
        reason: SubscriptionTier.LIFETIME,
      },
      originalStore: userStore ?? 'stripe',
    };

  /**
   * If the user's store doesn't match the current store, they can't alter or manage their subscription.
   * E.g. on the web (stripe), the user can't manage their subscription if they subscribed on the App Store or Play Store.
   */
  if (userStore && currentDeviceStore !== userStore)
    return {
      canAlterSubscription: {
        allowed: false,
        reason: 'store_mismatch',
      },
      canManageSubscription: {
        allowed: false,
        reason: 'store_mismatch',
      },
      originalStore: userStore,
    };

  /**
   * User is in a native app with RevenueCat available, but there's no customer info, this is a bug, hopefully temporary,
   * e.g. loading state or network error.
   */
  if (isRevenueCatAvailable && !customerInfo)
    return {
      canAlterSubscription: {
        allowed: false,
        reason: 'revenuecat_unavailable',
      },
      canManageSubscription: {
        allowed: false,
        reason: 'revenuecat_unavailable',
      },
      originalStore: currentDeviceStore,
    };

  /**
   * User is not in a native app, or is in a native app without RevenueCat available, this is considered as Stripe store.
   * The above issues are the only that could affect this, but since we are here it means they didn't trip the above checks,
   * and everything is fine.
   */
  if (!isRevenueCatAvailable)
    return {
      canAlterSubscription: {
        allowed: true,
      },
      canManageSubscription: {
        allowed: true,
      },
      originalStore: currentDeviceStore,
    };

  /**
   * Native app with RevenueCat available, woody reports that the user has an active subscription, but no revenuecat entitlement was found.
   * This is most likely because the user is subscribed to a plan on another Fabric account.
   * But could also happen if woody is out of sync with revenuecat.
   */
  if (!userEntitlement && userTier !== SubscriptionTier.FREE) {
    return {
      canAlterSubscription: {
        allowed: false,
        reason: 'revenuecat_missing_entitlement',
      },
      canManageSubscription: {
        allowed: true,
      },
      originalStore: currentDeviceStore,
    };
  }

  /**
   * Native app with RevenueCat available, woody reports that the user has *no* active subscription, but revenuecat reports there are subscriptions active.
   * This is most likely because the user is subscribed to a plan on another Fabric account.
   * But could also happen if woody is out of sync with revenuecat.
   */
  if (userTier === SubscriptionTier.FREE && customerInfo!.activeSubscriptions.length > 0) {
    return {
      canAlterSubscription: {
        allowed: false,
        reason: 'revenuecat_subscription_mismatch_woody',
      },
      canManageSubscription: {
        allowed: true,
      },
      originalStore: currentDeviceStore,
    };
  }

  /**
   * Native app with RevenueCat available, user has an active subscription, and revenuecat entitlement was found.
   * But the originalAppUserId from the customer info doesn't match the user's id.
   * This is definitely because the user is subscribed to a plan on another Fabric account, compared to the above
   * scenarios this one is more reliably known, as we can compare the user's id with the originalAppUserId.
   */
  if (user.id !== customerInfo!.originalAppUserId) {
    return {
      canAlterSubscription: {
        allowed: false,
        reason: 'revenuecat_user_mismatch',
      },
      canManageSubscription: {
        allowed: true,
      },
      originalStore: currentDeviceStore,
    };
  }

  // This is the happy path, nothing looks out of place, user can alter and manage their subscription.
  return {
    canAlterSubscription: {
      allowed: true,
    },
    canManageSubscription: {
      allowed: true,
    },
    originalStore: currentDeviceStore,
    userEntitlement: userEntitlement ?? undefined,
  };
};
